import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#lib/utils'

const alertVariants = cva(
	"group/alert relative grid w-full gap-0.5 rounded-lg border px-4 py-3 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2.5 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
	{
		variants: {
			// The tones of Badge: the `-soft` tint under the `-strong` text, the icon in the same colour.
			variant: {
				default: 'bg-card text-card-foreground',
				destructive:
					'border-transparent bg-destructive-soft text-destructive *:data-[slot=alert-description]:text-current *:[svg]:text-current',
				success:
					'border-transparent bg-success-soft text-success-strong *:data-[slot=alert-description]:text-current *:[svg]:text-current',
				warning:
					'border-transparent bg-warning-soft text-warning-strong *:data-[slot=alert-description]:text-current *:[svg]:text-current',
				attention:
					'border-transparent bg-attention-soft text-attention-strong *:data-[slot=alert-description]:text-current *:[svg]:text-current',
				info: 'border-transparent bg-info-soft text-info-strong *:data-[slot=alert-description]:text-current *:[svg]:text-current'
			}
		},
		defaultVariants: {
			variant: 'default'
		}
	}
)

function Alert({
	className,
	variant,
	...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
	return (
		<div
			className={cn(alertVariants({ variant }), className)}
			data-slot='alert'
			role='alert'
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
				className
			)}
			data-slot='alert-title'
			{...props}
		/>
	)
}

function AlertDescription({
	className,
	...props
}: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn(
				'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
				className
			)}
			data-slot='alert-description'
			{...props}
		/>
	)
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			className={cn('absolute top-2.5 right-3', className)}
			data-slot='alert-action'
			{...props}
		/>
	)
}

export { Alert, AlertTitle, AlertDescription, AlertAction }
